import { useAtom } from 'jotai';
import { useMemo } from 'react';
import type { SSOImageRepositoryResponse } from '@/shared/api/generated';
import { useGetApiV1Ssoimage } from '@/shared/api/generated/hooks/SSOImageController/useGetApiV1Ssoimage';
import type { GetApiV1SsoimageQueryParams } from '@/shared/api/generated/types/SSOImageController/GetApiV1Ssoimage';
import { libraryFiltersAtom } from '../store/atoms/libraryFiltersAtom';
import type { LibrarySlide } from '../store/atoms/slideLibraryAtom';

/**
 * Hook principal de busca da biblioteca de slides via API.
 *
 * Além de listar os slides com `useGetApiV1Ssoimage`, também faz fetch paralelo
 * de todos os JSONs de canvas referenciados em `urlStorage` de cada variante de idioma,
 * populando `variants[key].canvas` de cada `LibrarySlide` com o conteúdo Fabric.js.
 *
 * Aplica filtragem e ordenação client-side usando o `libraryFiltersAtom`.
 *
 * @param params - Parâmetros de paginação (pageNumber, pageSize). Padrão: página 1, 50 itens.
 * @returns Slides com variantes preenchidas, estados de loading/error e metadados de paginação.
 *
 * @example
 * ```tsx
 * const { allSlides, isLoading, hasResults } = useSlideLibraryQuery();
 * ```
 */
const PAGE_SIZE = 12;

export function useSlideLibraryQuery(params?: GetApiV1SsoimageQueryParams) {
  const [filters] = useAtom(libraryFiltersAtom);

  const {
    data,
    isLoading: isListLoading,
    isError,
  } = useGetApiV1Ssoimage({
    pageNumber: params?.pageNumber ?? 1,
    pageSize: params?.pageSize ?? PAGE_SIZE,
  });

  const allSlides = useMemo<LibrarySlide[]>(() => {
    const raw = (data?.data ?? []) as Required<SSOImageRepositoryResponse>[];

    const { appliedQuery, appliedCategoryId, sortBy } = filters;
    const query = appliedQuery?.trim().toLowerCase() ?? '';

    // 🔄 MAPEAMENTO — espelha status do objeto raiz para statusId dos cards
    let mapped: LibrarySlide[] = raw.map(item => {
      const statusId = item.status;
      return {
        ...item,
        ...(statusId !== undefined ? { statusId } : {}),
      };
    });

    // 🔍 Filtro por categoria
    if (appliedCategoryId !== null) {
      mapped = mapped.filter(s => s.categoryId === appliedCategoryId);
    }

    // 🔍 Filtro por busca
    if (query) {
      mapped = mapped.filter(s => s.title?.toLowerCase().includes(query));
    }

    // 🔤 Ordenação
    if (sortBy === 'title') {
      return [...mapped].sort((a, b) => (a.title ?? '').localeCompare(b.title ?? '', 'pt-BR'));
    }

    return mapped;
  }, [data, filters]);

  return {
    allSlides,
    isLoading: isListLoading,
    isError,
    hasResults: allSlides.length > 0,
    currentPage: data?.currentPage,
    totalPages: data?.totalPages,
    totalCount: data?.totalCount,
  } as const;
}
